import { takeLatest, put, call, all, select } from 'redux-saga/effects';
import filter from 'lodash/filter';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { isGuestUser } from 'yoda-core-components/lib/helpers/Utils/Utils';
import { addItemToCart } from 'yoda-interfaces/lib/Cart/CartApi';
import { removeSavedItem } from 'yoda-interfaces/lib/SavedItems/SavedItemsApi';
import showGlobalLoader from 'yoda-checkout-components/lib/actions/ShowGlobalLoaderAction';
import FactorySaga from 'yoda-site-components/lib/sagas/FactorySaga';
import { fetchSavedItems } from '../actions/SaveForLaterAction';
import {
    digitalAnalyticsData,
    updateAnalyticsClickAction,
} from 'yoda-checkout-components/lib/actions/CheckoutAnalyticsAction';
import TokenProvider from 'yoda-core-components/lib/helpers/TokenProvider/TokenProvider';
import {
    SET_SERVICE_ERROR,
    DP_CHECKOUT_SAVED_ITEMS,
    GENERIC_API_ERROR,
    DP_CHECKOUT_SAVED_ITEMS_PAYLOAD,
} from 'yoda-site-components/lib/common/Constants';
import { ANALYTICS_ACTIONS_MOVE_TO_CART } from 'yoda-checkout-components/lib/common/Constants';
import { REMOVE_SAVED_ITEM_REQUEST } from 'yoda-checkout-components/lib/actionTypes/RemoveSavedItemActionTypes';

import {
    CART_DETAILS_GET_REQUEST,
    REMOVE_SAVED_ITEM_SUCCESS,
    REMOVE_CART_ITEM_ERROR,
    RESET_MOVE_SFL_CART_SUCCESS,
} from '../actionTypes/CartActionTypes';

function doDeleteSavedItem(itemData) {
    let savedItemObj = {};
    let savedItems = [];
    if (TokenProvider.get(DP_CHECKOUT_SAVED_ITEMS)) {
        savedItemObj = JSON.parse(TokenProvider.get(DP_CHECKOUT_SAVED_ITEMS));
    }
    if (TokenProvider.get(DP_CHECKOUT_SAVED_ITEMS_PAYLOAD)) {
        savedItems = JSON.parse(TokenProvider.get(DP_CHECKOUT_SAVED_ITEMS_PAYLOAD));
    }
    if (savedItemObj && savedItemObj[itemData.ppId]) {
        delete savedItemObj[itemData.ppId];
    }
    const filteredItems = filter(
        savedItems,
        (item) => item.product.id !== itemData.ppId || item.itemId !== itemData.skuId
    );
    TokenProvider.set(DP_CHECKOUT_SAVED_ITEMS, JSON.stringify(savedItemObj));
    TokenProvider.set(DP_CHECKOUT_SAVED_ITEMS_PAYLOAD, JSON.stringify(filteredItems));
}

function setServiceErrorMessage(errorInfo, action) {
    const errorHandlerInfo = action.payload.errorHandler;
    errorHandlerInfo.errorCode = isEmpty(errorInfo.errorCode)
        ? GENERIC_API_ERROR
        : errorInfo.errorCode;
    errorHandlerInfo.errorMessage = errorInfo.errorMessage;
    return errorHandlerInfo;
}

function* removeItemFromSFL(action) {
    const removeSflItemAction = {};
    removeSflItemAction.payload = {
        params: action.payload.itemId,
        cartSavedItems: true,
        errorHandler: action.payload.errorHandler,
    };
    let sflResult = {};
    if (!isGuestUser()) {
        sflResult = yield call(FactorySaga, removeSavedItem, removeSflItemAction);
    } else {
        yield call(doDeleteSavedItem, action.payload.data[0]);
        sflResult.isSuccess = true;
    }
    if (sflResult.isSuccess) {
        yield all([
            put(fetchSavedItems()),
            put(showGlobalLoader(false)),
            !action.payload.isRemoveItemFromSFL && put({ type: REMOVE_SAVED_ITEM_SUCCESS }),
            action.payload.isRemoveItemFromSFL &&
                put({ type: CART_DETAILS_GET_REQUEST, requestType: 'REMOVE_SFL_ITEM' }),
        ]);
    } else {
        const errorInfo = get(sflResult.response.data, 'data[0].errors[0]');
        if (errorInfo) {
            const errorHandlerInfo = yield call(setServiceErrorMessage, errorInfo, action);
            yield put({ type: SET_SERVICE_ERROR, errorHandlerInfo });
        }
        yield put(showGlobalLoader(false));
    }
}

function* removeSavedItemSaga(actionParam) {
    const action = actionParam;
    action.payload.errorHandler = {
        context: 'CART',
        errorComponent: 'CART_SECTION',
    };
    action.payload.errorHandler.errorComponent = 'SFL_SECTION';
    action.payload.errorHandler.index = action.payload.itemId;
    const context = (state) => state.context;
    const appContext = yield select(context);
    const { enablePreSourcing = true } = appContext?.featureFlags || {};
    try {
        if (!action.payload.isRemoveItemFromSFL) {
            const addItemAction = {};
            addItemAction.payload = {
                data: action.payload.data,
                source: 'sfl',
                errorHandler: action.payload.errorHandler,
            };
            if (enablePreSourcing) {
                addItemAction.payload.xClientSource = 'CART';
            }
            yield put({ type: RESET_MOVE_SFL_CART_SUCCESS });
            const addItemResult = yield call(FactorySaga, addItemToCart, addItemAction);
            if (addItemResult.isSuccess) {
                // DA ==> adding moveToCartItemSkuId to redux store. We will need this for Analytics
                const moveToCartItemSkuId = action.payload.data[0].skuId;
                yield all([
                    put(digitalAnalyticsData(moveToCartItemSkuId)),
                    put({ type: CART_DETAILS_GET_REQUEST, requestType: 'ADD_SFL_ITEM' }),
                ]);
                yield put(updateAnalyticsClickAction(ANALYTICS_ACTIONS_MOVE_TO_CART));
                yield call(removeItemFromSFL, action);
            } else {
                const errorInfo = get(addItemResult.response.data, 'data[0].errors[0]');
                if (errorInfo) {
                    action.payload.errorHandler.errorComponentIndex = action.payload.itemId;
                    const errorHandlerInfo = yield call(setServiceErrorMessage, errorInfo, action);
                    yield put({ type: SET_SERVICE_ERROR, errorHandlerInfo });
                }
                yield put(showGlobalLoader(false));
            }
        } else {
            yield call(removeItemFromSFL, action);
        }
    } catch (error) {
        yield all([
            // Need to show appropriate error in this case
            put({ type: REMOVE_CART_ITEM_ERROR, error }),
            put(showGlobalLoader(false)),
        ]);
    }
}

const watchRemoveSavedItem = function* watchRemoveSavedItem() {
    yield takeLatest(REMOVE_SAVED_ITEM_REQUEST, removeSavedItemSaga);
};
watchRemoveSavedItem.sagaName = 'watchRemoveSavedItem';

export default watchRemoveSavedItem;
